import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Trash2, Download, Upload, Loader2, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  fetchBookingThread, sendBookingMessage, markBookingThreadRead, uploadBookingDocument, removeBookingDocument,
  type BookingThread,
} from '@/services/readerApi';

// Messages and sides on a reader booking, for both sides of it. Restores what
// the casting-era request had (a comment thread and a document list), on the
// Plus booking record instead.

const MAX_FILE = 25 * 1024 * 1024;
const when = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
};
const kb = (n: number) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function BookingThreadDialog({ open, onClose, bookingId, otherName, onChanged }: {
  open: boolean; onClose: () => void; bookingId: string | null; otherName: string; onChanged?: () => void;
}) {
  const { toast } = useToast();
  const [thread, setThread] = useState<BookingThread | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!bookingId) return;
    try { setThread(await fetchBookingThread(bookingId)); } catch { /* keep what we have */ }
  }, [bookingId]);

  useEffect(() => {
    if (!open || !bookingId) return;
    load();
    markBookingThreadRead(bookingId).then(() => onChanged?.());
    const itv = setInterval(load, 15000);
    return () => clearInterval(itv);
  }, [open, bookingId, load]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread?.messages.length]);

  const send = async () => {
    if (!bookingId || !text.trim()) return;
    setSending(true);
    try {
      setThread(await sendBookingMessage(bookingId, text.trim()));
      setText('');
      onChanged?.();
    } catch (e: any) {
      toast({ title: 'Could not send', description: e.message, variant: 'destructive' });
    } finally { setSending(false); }
  };

  const upload = async (files: FileList | null) => {
    if (!bookingId || !files || !files.length) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        if (f.size > MAX_FILE) { toast({ title: `${f.name} is over 25MB`, variant: 'destructive' }); continue; }
        setThread(await uploadBookingDocument(bookingId, f));
      }
      toast({ title: 'Sides uploaded', description: `${otherName} has been told.` });
      onChanged?.();
    } catch (e: any) {
      toast({ title: 'Upload failed', description: e.message, variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const remove = async (docId: string) => {
    if (!bookingId || !confirm('Remove this file?')) return;
    try { setThread(await removeBookingDocument(bookingId, docId)); onChanged?.(); }
    catch (e: any) { toast({ title: 'Could not remove', description: e.message, variant: 'destructive' }); }
  };

  const ink = 'var(--hj-beige-ink)', ink2 = 'var(--hj-beige-ink-2)', line = 'var(--hj-beige-line)';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg p-0" data-paper="beige" style={{ background: 'var(--hj-beige)', border: `1px solid ${line}`, color: ink }}>
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="font-hj-display" style={{ fontSize: 20, color: ink, letterSpacing: '-0.01em' }}>
            {otherName}
          </DialogTitle>
        </DialogHeader>

        {/* Sides */}
        <div
          className="mx-6"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files); }}
          style={{ padding: 12, borderRadius: 'var(--hj-r-sm)', border: `1.5px ${dragOver ? 'solid' : 'dashed'} ${dragOver ? ink : line}`, background: dragOver ? 'var(--hj-beige-deep)' : 'transparent' }}
        >
          <div className="flex items-center justify-between gap-3">
            <span className="mono-label" style={{ color: ink }}>Sides{thread?.documents.length ? ` · ${thread.documents.length}` : ''}</span>
            <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5"
              style={{ padding: '6px 12px', borderRadius: 999, border: `1px solid ${line}`, background: '#FFFFFF', color: ink, fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
            </button>
            <input ref={fileRef} type="file" multiple hidden accept=".pdf,.doc,.docx,.txt,.rtf,.pages,image/*" onChange={(e) => { upload(e.target.files); e.currentTarget.value = ''; }} />
          </div>
          {thread && thread.documents.length > 0 ? (
            <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
              {thread.documents.map(d => (
                <div key={d.id} className="flex items-center gap-2" style={{ fontSize: 13 }}>
                  <FileText className="w-4 h-4 flex-shrink-0" style={{ color: ink2 }} />
                  <a href={d.url} download={d.name} className="truncate" style={{ color: ink, fontWeight: 600, textDecoration: 'none' }}>{d.name}</a>
                  <span style={{ color: ink2, fontSize: 11.5, whiteSpace: 'nowrap' }}>{kb(d.size)} · {d.byName}</span>
                  <a href={d.url} download={d.name} title="Download" className="ml-auto inline-flex" style={{ color: ink2 }}><Download className="w-4 h-4" /></a>
                  {d.mine && <button type="button" onClick={() => remove(d.id)} title="Remove" style={{ background: 'none', border: 0, color: ink2, cursor: 'pointer', padding: 0 }}><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
            </div>
          ) : (
            <p className="m-0" style={{ marginTop: 8, fontSize: 12.5, color: ink2 }}>Drop the sides here, or upload them. PDF, Word, text or images, up to 25MB each.</p>
          )}
        </div>

        {/* Messages */}
        <div className="px-6" style={{ maxHeight: 320, overflowY: 'auto', marginTop: 14 }}>
          {!thread ? (
            <div className="flex justify-center" style={{ padding: 20 }}><Loader2 className="w-5 h-5 animate-spin" style={{ color: ink2 }} /></div>
          ) : thread.messages.length === 0 ? (
            <p className="m-0 text-center" style={{ padding: '18px 0', fontSize: 13, color: ink2 }}>No messages yet. Say hello, or send the sides above.</p>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {thread.messages.map(m => (
                <div key={m.id} style={{ display: 'flex', justifyContent: m.mine ? 'flex-end' : 'flex-start' }}>
                  <div style={{ maxWidth: '82%', padding: '9px 12px', borderRadius: 12, background: m.mine ? 'var(--hj-yellow)' : '#FFFFFF', border: m.mine ? 'none' : `1px solid ${line}`, color: '#0A0A0A' }}>
                    <div style={{ fontSize: 13.5, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{m.text}</div>
                    <div style={{ fontSize: 10.5, marginTop: 4, color: m.mine ? 'oklch(0.3 0.02 80)' : ink2 }}>{m.mine ? 'You' : m.byName} · {when(m.at)}</div>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="flex items-end gap-2 px-6 pb-6" style={{ marginTop: 12 }}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Message ${otherName}…`}
            rows={2}
            style={{ flex: 1, resize: 'none', padding: '10px 12px', borderRadius: 10, border: `1px solid ${line}`, background: '#FFFFFF', color: '#0A0A0A', fontSize: 13.5, fontFamily: 'inherit' }}
          />
          <button type="button" onClick={send} disabled={sending || !text.trim()} className="inline-flex items-center justify-center"
            style={{ width: 42, height: 42, borderRadius: 999, border: 0, background: 'var(--hj-yellow)', color: '#0A0A0A', cursor: text.trim() ? 'pointer' : 'not-allowed', opacity: text.trim() ? 1 : 0.5 }}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
